import React, { useState, useEffect } from 'react';
import { AppSection } from '../types';

interface TalentPageProps {
  onNavigate: (section: AppSection) => void;
  onContactClick: () => void;
}

const TalentPage: React.FC<TalentPageProps> = ({ onNavigate, onContactClick }) => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate real-time talent grid synchronization
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const models = [
    { 
      title: "Individual Augmentation", 
      desc: "Specific experts embedded in your team to fill critical skill gaps.",
      usage: "Backend, UI/UX, Cloud Infrastructure",
      accent: "#0091da"
    },
    { 
      title: "Managed Squads", 
      desc: "Full, self-sufficient teams including a PM and Lead Architect.",
      usage: "Product MVP, Platform Refactoring",
      accent: "#facc15"
    },
    { 
      title: "CTO-as-a-Service", 
      desc: "Fractional strategic leadership for scaling startups and SMEs.",
      usage: "Roadmap Planning, Funding Support",
      accent: "#0091da"
    }
  ];

  return (
    <div className="bg-white">
      <section className="py-32 border-b border-slate-100 bg-white relative overflow-hidden">
        <div className="absolute right-0 top-0 w-1/3 h-full bg-slate-50 opacity-40 -skew-x-12 transform translate-x-24"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl animate-in fade-in slide-in-from-bottom-8 duration-1000">
            <div className="mono text-xs text-[#0091da] font-bold mb-6 tracking-widest uppercase">// Engine 02: Managed Talent</div>
            <h1 className="text-6xl md:text-8xl font-bold text-slate-950 mb-8 tracking-tighter leading-[0.9]">Engineered for Velocity</h1>
            <p className="text-xl text-slate-600 font-light leading-relaxed max-w-2xl">
              Access the top 2% of Chicago-based and global technical practitioners. Every Hunargrid expert is backed by our internal senior leadership council.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50 relative">
        <div className="absolute inset-0 grid-bg opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <h2 className="mono text-xs text-slate-400 font-bold uppercase tracking-[0.4em] mb-4">// Engagement Models</h2>
            <div className="h-1 w-24 bg-[#facc15] mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white p-10 border border-slate-100 rounded-sm">
                  <div className="w-12 h-1 mb-10 skeleton"></div>
                  <div className="h-8 w-3/4 skeleton mb-4 rounded"></div>
                  <div className="space-y-2 mb-10">
                    <div className="h-3 w-full skeleton rounded"></div>
                    <div className="h-3 w-5/6 skeleton rounded"></div>
                  </div>
                  <div className="pt-8 border-t border-slate-100">
                    <div className="h-2 w-16 skeleton mb-3 rounded"></div>
                    <div className="h-4 w-1/2 skeleton rounded"></div>
                  </div>
                </div>
              ))
            ) : (
              models.map((model, i) => (
                <div key={i} className="bg-white p-10 border border-slate-100 hover:border-slate-300 transition-all shadow-sm hover:shadow-xl group rounded-sm animate-in slide-in-from-bottom-4" style={{ animationDelay: `${i * 100}ms` }}>
                  <div 
                    className="w-12 h-1 mb-10 transition-all group-hover:w-full" 
                    style={{ backgroundColor: model.accent }}
                  ></div>
                  <h3 className="text-2xl font-bold text-slate-950 mb-4">{model.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-10 font-light">{model.desc}</p>
                  <div className="pt-8 border-t border-slate-100">
                    <div className="mono text-[10px] text-slate-400 uppercase mb-3 tracking-widest">Primary Context</div>
                    <div className="text-xs font-bold text-slate-900 uppercase tracking-widest flex items-center">
                      <span className="w-1.5 h-1.5 rounded-full mr-3" style={{ backgroundColor: model.accent }}></span>
                      {model.usage}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#020617] rounded-sm p-12 md:p-24 text-white relative overflow-hidden shadow-2xl">
             <div className="absolute inset-0 grid-bg-dark opacity-10"></div>
             <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#facc15]/5 rounded-full blur-[100px]"></div>
             
             <div className="relative z-10 text-center max-w-2xl mx-auto">
                <h2 className="text-5xl font-bold mb-8 tracking-tight">Ready to browse the Grid?</h2>
                <p className="text-slate-400 mb-12 text-lg font-light leading-relaxed">
                  Our talent availability is real-time. Get a customized shortlist based on your specific technical requirements and cultural alignment.
                </p>
                <button 
                  onClick={onContactClick}
                  className="btn-brand-primary px-16 py-6 font-bold uppercase tracking-widest text-xs rounded-sm"
                >
                  Request Talent Shortlist
                </button>
             </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TalentPage;